// features/judge-assist/RulingsManager.tsx
// Admin tool: manage the organization's house rulings + the per-user daily
// limit, and review answer feedback. House rulings are stored in Realtime
// Database (judgeAssist/rulings/onePiece) and merged into Judge Assist's
// answers; feedback (judgeAssist/feedback) is read-only here.
import { ReactElement, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, TextField, Typography, alpha } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { limitToLast, onValue, orderByKey, push, query, ref, remove, set } from 'firebase/database';

import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';

interface HouseRuling {
  id: string;
  q: string;
  a: string;
  source?: string;
}

interface FeedbackItem {
  id: string;
  query: string;
  response: string;
  rating: string;
  ts?: string;
}

const GAME = 'onePiece';
const PATH = `judgeAssist/rulings/${GAME}`;

export function RulingsManager(): ReactElement {
  const { user } = useAuth();
  const [tab, setTab] = useState<'rulings' | 'feedback'>('rulings');
  const [items, setItems] = useState<HouseRuling[]>([]);
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [q, setQ] = useState('');
  const [a, setA] = useState('');
  const [source, setSource] = useState('');
  const [saving, setSaving] = useState(false);
  const [dailyLimit, setDailyLimit] = useState('20');
  const [savingLimit, setSavingLimit] = useState(false);

  useEffect(() => {
    const unsub = onValue(ref(db, 'config/judgeAssist/dailyLimit'), (snap) => {
      const v = snap.val();
      setDailyLimit(v === null || v === undefined ? '20' : String(v));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onValue(ref(db, PATH), (snap) => {
      const val = (snap.val() || {}) as Record<string, { q?: string; a?: string; source?: string }>;
      setItems(
        Object.entries(val)
          .filter(([, v]) => v && v.q && v.a)
          .map(([id, v]) => ({ id, q: v.q as string, a: v.a as string, source: v.source })),
      );
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const fbQuery = query(ref(db, 'judgeAssist/feedback'), orderByKey(), limitToLast(50));
    const unsub = onValue(fbQuery, (snap) => {
      const val = (snap.val() || {}) as Record<string, Partial<FeedbackItem>>;
      setFeedback(
        Object.entries(val)
          .map(([id, v]) => ({
            id,
            query: v.query || '',
            response: v.response || '',
            rating: String(v.rating ?? ''),
            ts: v.ts,
          }))
          .reverse(), // newest first
      );
    });
    return () => unsub();
  }, []);

  const saveLimit = async (): Promise<void> => {
    const n = Number(dailyLimit);
    if (!Number.isFinite(n) || n < 0 || savingLimit) return;
    setSavingLimit(true);
    try {
      await set(ref(db, 'config/judgeAssist/dailyLimit'), Math.floor(n));
    } finally {
      setSavingLimit(false);
    }
  };

  const add = async (): Promise<void> => {
    if (!q.trim() || !a.trim() || saving) return;
    setSaving(true);
    try {
      await push(ref(db, PATH), {
        q: q.trim(),
        a: a.trim(),
        source: source.trim() || 'House ruling',
        verifiedBy: user?.uid || null,
        verifiedAt: new Date().toISOString(),
      });
      setQ('');
      setA('');
      setSource('');
    } finally {
      setSaving(false);
    }
  };

  const del = async (id: string): Promise<void> => {
    await remove(ref(db, `${PATH}/${id}`));
  };

  // Turn a flagged answer into a house ruling: prefill the question and jump to
  // the Rulings tab so the admin can write the correct ruling.
  const applyAsRuling = (fb: FeedbackItem): void => {
    setQ(fb.query);
    setA('');
    setSource('Correction from flagged answer');
    setTab('rulings');
  };

  const downCount = feedback.filter((f) => f.rating === 'down').length;

  const tabSx = (active: boolean) => ({
    flex: 1,
    textTransform: 'none' as const,
    fontSize: 12.5,
    fontWeight: 700,
    borderRadius: 1.5,
    color: active ? 'primary.main' : 'text.secondary',
    backgroundColor: (t: { palette: { primary: { main: string } } }) =>
      active ? alpha(t.palette.primary.main, 0.12) : 'transparent',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Tabs */}
      <Box sx={{ display: 'flex', gap: 0.5, p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Button size="small" onClick={() => setTab('rulings')} sx={tabSx(tab === 'rulings')}>
          Rulings
        </Button>
        <Button size="small" onClick={() => setTab('feedback')} sx={tabSx(tab === 'feedback')}>
          Feedback{downCount > 0 ? ` · ${downCount} 👎` : ''}
        </Button>
      </Box>

      <Box sx={{ p: 1.5, overflowY: 'auto', flex: 1 }}>
        {tab === 'rulings' ? (
          <>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.6, mb: 1.5 }}>
              The built-in official rulings are always active. Add your organization's own house
              rulings here — they're merged into Judge Assist's answers and marked org-verified.
            </Typography>

            {/* Per-user daily limit */}
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.card',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>Daily limit per user</Typography>
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                  Questions / user / day. Admins are exempt. 0 = unlimited.
                </Typography>
              </Box>
              <TextField
                value={dailyLimit}
                onChange={(e) => setDailyLimit(e.target.value)}
                size="small"
                type="number"
                inputProps={{ min: 0 }}
                sx={{ width: 76, '& .MuiOutlinedInput-root': { fontSize: 13 } }}
              />
              <Button
                onClick={() => void saveLimit()}
                disabled={savingLimit}
                variant="outlined"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Save
              </Button>
            </Box>

            {/* Add form */}
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.card',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <TextField
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Situation / question"
                size="small"
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
              />
              <TextField
                value={a}
                onChange={(e) => setA(e.target.value)}
                placeholder="Ruling"
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
              />
              <TextField
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="Source (optional) — e.g. HJ decision, official Q&A"
                size="small"
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 12.5 } }}
              />
              <Button
                onClick={() => void add()}
                disabled={!q.trim() || !a.trim() || saving}
                startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}
                variant="contained"
                size="small"
                sx={{ alignSelf: 'flex-end', textTransform: 'none' }}
              >
                Add ruling
              </Button>
            </Box>

            {/* List */}
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: 'text.disabled', mb: 1 }}>
              CUSTOM RULINGS ({items.length})
            </Typography>
            {items.length === 0 && (
              <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                None yet. Add your first house ruling above.
              </Typography>
            )}
            {items.map((r) => (
              <Box
                key={r.id}
                sx={{
                  p: 1.25,
                  mb: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                  display: 'flex',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.25 }}>{r.q}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5 }}>{r.a}</Typography>
                  {r.source && (
                    <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.5 }}>{r.source}</Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => void del(r.id)}
                  aria-label="Delete ruling"
                  sx={{ color: 'error.main', alignSelf: 'flex-start', '&:hover': { backgroundColor: (t) => alpha(t.palette.error.main, 0.1) } }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ))}
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.6, mb: 1.5 }}>
              Recent answer feedback. For a flagged (👎) answer, tap “Use as house ruling” to write
              the correct ruling — it will override future answers.
            </Typography>
            {feedback.length === 0 && (
              <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>No feedback yet.</Typography>
            )}
            {feedback.map((fb) => {
              const down = fb.rating === 'down';
              return (
                <Box
                  key={fb.id}
                  sx={{
                    p: 1.25,
                    mb: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: down ? (t) => alpha(t.palette.error.main, 0.4) : 'divider',
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    {down ? (
                      <ThumbDownAltOutlinedIcon sx={{ fontSize: 15, color: 'error.main' }} />
                    ) : (
                      <ThumbUpAltOutlinedIcon sx={{ fontSize: 15, color: 'success.main' }} />
                    )}
                    {fb.ts && (
                      <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                        {new Date(fb.ts).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.25 }}>
                    {fb.query || '(no question recorded)'}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {fb.response}
                  </Typography>
                  {down && (
                    <Button
                      onClick={() => applyAsRuling(fb)}
                      startIcon={<AutoFixHighIcon sx={{ fontSize: 16 }} />}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1, textTransform: 'none', fontSize: 12 }}
                    >
                      Use as house ruling
                    </Button>
                  )}
                </Box>
              );
            })}
            {feedback.length > 0 && (
              <Chip
                label={`${feedback.length} most recent`}
                size="small"
                sx={{ mt: 1, fontSize: 10, color: 'text.disabled' }}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
